import { redirect } from '@tanstack/react-router';

import { track } from '@shared/events';
import config from '@/config';
import { AUTH_KEY } from '@shared/auth';

/**
 * Guard for `login`/`jwt` auth types (`config.app.auth.type`) -- reads the
 * same `AUTH_KEY` localStorage flag a tenant's own home page sets on a
 * successful `authenticateUser()` call
 * (`features/direct-login/ui/authentication-dialog.tsx`'s `onSuccess`), and
 * sends an unauthenticated reader home instead of letting them load a
 * protected route directly.
 *
 * That render-time gate (e.g. `templates/poets/home/home.tsx` swapping its
 * entire tree for `<AuthenticationView>` while unauthenticated) only runs
 * for readers who land on `/` first -- a direct or bookmarked navigation to
 * `/issues/$id` or `/issues/$id/$articleId` reaches that route's own
 * component instead and skips it entirely. This is the `beforeLoad`
 * equivalent for those routes.
 *
 * Unlike `token-auth-guard.ts`'s `tokenAuthGuard`, there's no `?token=` URL
 * exchange to perform here -- `login`/`jwt` authenticate through the on-page
 * form, not a query param, so this is just the read-and-redirect half.
 *
 * Not self-gated on `config.app.auth.type` here beyond the `enabled` check
 * -- it's registered per auth type via `TemplateAuthWiring.guards` (see
 * `route-protection.ts`'s `resolveProtection`), which already only calls
 * this for tenants on `login`/`jwt`; re-checking the type here would just
 * repeat that decision in a second place.
 */
export function loginAuthGuard(): void {
	if (!config?.app?.auth?.enabled) {
		return;
	}

	if (localStorage.getItem(AUTH_KEY)) {
		return;
	}

	// A route the reader may not open without signing in.
	track('paywall_hit', { gate: 'route', reason: 'login_required' });
	throw redirect({ to: '/', replace: true });
}
